import { createFileRoute } from '@tanstack/react-router'
import { DeepInsightsTab } from '@workspace/seedforge-features/analytics'
import { ProfileView } from '@workspace/seedforge-features/profile'
import { cn, getInitials } from '@workspace/ui/lib/utils'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminDataRoomAnalyticsQuery,
  useAdminDataRoomByIdQuery,
  useAdminPublicProfileFullQuery,
} from '../../../features/admin/hooks/use-admin-api'

export const Route = createFileRoute('/admin/data-rooms/$id')({
  component: Page,
})

const tabs = [
  { key: 'overview', label: 'Overview' },
  { key: 'analytics', label: 'Analytics' },
] as const

type Tab = (typeof tabs)[number]['key']

function Page() {
  const { id } = Route.useParams()
  const [activeTab, setActiveTab] = useState<Tab>('overview')
  const adminQuery = useAdminDataRoomByIdQuery(id)

  if (adminQuery.isLoading)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  if (!adminQuery.data?.data)
    return <div className="py-20 text-center text-muted-foreground">Not found</div>

  const token = adminQuery.data.data.dataRoom.token

  return (
    <>
      <nav className="border-b border-border">
        <div className="-mb-px flex gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>
      <div className="mt-6">
        {activeTab === 'overview' && <DataRoomPreview token={token} />}
        {activeTab === 'analytics' && <AdminAnalyticsTab dataRoomId={id} />}
      </div>
    </>
  )
}

function DataRoomPreview({ token }: { token: string }) {
  const { data, isLoading } = useAdminPublicProfileFullQuery(token)

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!data?.data) {
    return <div className="py-20 text-center text-muted-foreground">Failed to load profile</div>
  }

  const profile = data.data
  const { startup } = profile

  return (
    <div className="mx-auto max-w-5xl py-4">
      <ProfileView
        profile={{
          initials: startup.initials,
          name: startup.name,
          sector: '',
          sectorLabel: startup.categoryName ?? undefined,
          stage: startup.productStage ?? '',
          raisingStatus: startup.isFundraising,
          location: startup.location ?? '',
          founded: startup.foundingDate ?? '',
          website: startup.websiteUrl ?? '',
          description: startup.description ?? '',
          tags: startup.tags,
        }}
        fundraising={{
          round: startup.fundraisingStage ?? '',
          target: startup.fundraisingAmount
            ? `$${Number(startup.fundraisingAmount).toLocaleString()}`
            : '',
        }}
        pitchRecordings={
          profile.evaluation && profile.pitchSessionId
            ? [
                {
                  id: profile.pitchSessionId,
                  duration: profile.evaluation.pitchDuration || '30 min',
                  readinessLabel: '',
                  recordedDate: String(profile.evaluation.date),
                  verdictSnippet: '',
                  highlights: [],
                  judgeCount: 0,
                  topStrength: '',
                  topConcern: '',
                },
              ]
            : []
        }
        selectedPitchId={profile.pitchSessionId ?? ''}
        documents={profile.documents.map((d) => ({
          name: d.fileName,
          size: d.fileSize != null ? `${(d.fileSize / 1024).toFixed(0)} KB` : '',
          documentId: d.id,
          type: d.type ?? null,
          uploadStatus: 'ready' as const,
        }))}
        team={profile.teamMembers.map((m) => ({
          id: m.id,
          initials: getInitials(m.name),
          name: m.name,
          role: m.position ?? '',
          linkedInUrl: m.linkedinUrl ?? undefined,
          imageUrl: m.imageUrl,
          companyLogos: m.companyLogos,
          lastEducation: m.lastEducation,
          linkedinScrapeStatus: m.linkedinScrapeStatus,
        }))}
      />
    </div>
  )
}

function AdminAnalyticsTab({ dataRoomId }: { dataRoomId: string }) {
  const analyticsQuery = useAdminDataRoomAnalyticsQuery(dataRoomId)

  if (analyticsQuery.isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!analyticsQuery.data?.data) {
    return <div className="py-10 text-center text-sm text-muted-foreground">No analytics data</div>
  }

  const raw = analyticsQuery.data.data
  return <DeepInsightsTab aggregates={raw.aggregates} aiReport={null} aiReportGeneratedAt={null} />
}
